import React from 'react';

import clsx from 'clsx';
import { IconBaseProps } from 'react-icons';
import { makeStyles, Theme, createStyles } from '@material-ui/core';
import { alpha, darken, lighten } from '@material-ui/core/styles';

import { notificationBadge } from '@vyce/core/src/theme/styles';

export type LailaOrbState = 'idle' | 'thinking' | 'listening' | 'speaking' | 'offline';

interface Props {
  size?: number;
  state?: LailaOrbState;
  // How loud the user (listening) or Laila (speaking) is, 0–1; the halo swells with it.
  level?: number;
  // Unread replies; shown as a badge when above zero.
  unread?: number;
  // The soft glow around the orb; off where it would crowd small spaces.
  halo?: boolean;
  className?: string;
}

// Laila's mark, alive: a breathing halo, a turning ring and blinking eyes.
// The ring speeds up and the bars wave while she thinks; while she listens or
// speaks the orb swells with the voice.
export const LailaOrb: React.FC<Props> = ({
  size = 56,
  state = 'idle',
  level = 0,
  unread = 0,
  halo = true,
  className,
}) => {
  const classes = useStyles();

  return (
    <span
      className={clsx(classes.orb, className, {
        [classes.thinking]: state === 'thinking',
        [classes.listening]: state === 'listening',
        [classes.speaking]: state === 'speaking',
        [classes.offline]: state === 'offline',
      })}
      style={{ width: size, height: size, ['--level' as string]: level }}
      aria-hidden
    >
      {halo && <span className={classes.halo} />}
      <span className={classes.ring} />
      <span className={classes.core}>
        <svg className={classes.mark} viewBox="-0.5 4 57 52">
          <path className={classes.eye} d="M49.31 5.64L39.17 11.73L45.18 22L55.33 15.91Z" />
          <path className={classes.eye} d="M11.07 5.62L0.94 11.73L6.94 22.01L17.08 15.91Z" />
          <path className={classes.bar} d="M19.05 19.25L8.91 25.34L25.9 54.38L36.05 48.28Z" />
          <path
            className={clsx(classes.bar, classes.bar2)}
            d="M35.51 38.8L45.73 32.83L30.23 5.63L20.02 11.61Z"
          />
        </svg>
        <span className={classes.sheen} />
      </span>
      {unread > 0 && <span className={classes.badge}>{unread > 9 ? '9+' : unread}</span>}
    </span>
  );
};

// The orb in place of a react-icons glyph, e.g. a sidebar item. Keeps its own colours.
export const LailaOrbIcon = ({ size = 22, className }: IconBaseProps) => (
  <LailaOrb size={parseFloat(String(size))} halo={false} className={className} />
);

const useStyles = makeStyles((theme: Theme) => {
  const brand = theme.palette.primary.dark;
  const white = theme.palette.common.white;

  return createStyles({
    orb: {
      position: 'relative',
      display: 'inline-block',
      flex: 'none',
      '@media (prefers-reduced-motion: reduce)': {
        '& *': { animation: 'none !important' },
      },
    },
    halo: {
      position: 'absolute',
      inset: '-38%',
      borderRadius: '50%',
      background: `radial-gradient(closest-side, ${alpha(lighten(brand, 0.35), 0.55)}, ${alpha(
        brand,
        0.18
      )} 62%, ${alpha(brand, 0)})`,
      animation: '$breathe 4s ease-in-out infinite',
      pointerEvents: 'none',
    },
    ring: {
      position: 'absolute',
      inset: -2,
      borderRadius: '50%',
      background: `conic-gradient(from 0deg, ${brand}, ${lighten(brand, 0.55)} 22%, ${
        theme.palette.secondary.light
      } 40%, ${brand} 62%, ${brand})`,
      WebkitMask: `radial-gradient(farthest-side, transparent calc(100% - 2.5px), ${white} calc(100% - 2px))`,
      mask: `radial-gradient(farthest-side, transparent calc(100% - 2.5px), ${white} calc(100% - 2px))`,
      animation: '$spin 6s linear infinite',
    },
    core: {
      position: 'absolute',
      inset: 3,
      borderRadius: '50%',
      overflow: 'hidden',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: `radial-gradient(circle at 32% 26%, ${lighten(brand, 0.35)} 0%, ${brand} 52%, ${darken(
        brand,
        0.4
      )} 100%)`,
      boxShadow: `inset 0 1px 1px ${alpha(white, 0.35)}, 0 6px 18px ${alpha(brand, 0.35)}`,
    },
    mark: {
      width: '54%',
      height: '54%',
      overflow: 'visible',
      '& path': { fill: white, transformBox: 'fill-box', transformOrigin: 'center' },
    },
    eye: { animation: '$blink 6s infinite' },
    bar: {},
    bar2: {},
    sheen: {
      position: 'absolute',
      inset: 0,
      background: `linear-gradient(115deg, transparent 38%, ${alpha(white, 0.42)} 50%, transparent 62%)`,
      transform: 'translateX(-130%)',
      animation: '$sheen 5s ease-in-out infinite',
    },
    badge: {
      position: 'absolute',
      top: -4,
      right: -4,
      minWidth: 22,
      height: 22,
      padding: '0 6px',
      boxSizing: 'border-box',
      borderRadius: 11,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: notificationBadge.background,
      color: notificationBadge.text,
      fontSize: 12,
      fontWeight: 700,
      border: `2px solid ${theme.palette.background.paper}`,
      animation: '$pop 600ms cubic-bezier(.3,1.6,.5,1) both',
    },
    thinking: {
      '& $ring': { animationDuration: '1.1s' },
      '& $halo': { animation: '$breathe 1.4s ease-in-out infinite' },
      '& $bar': { animation: '$wave 900ms ease-in-out infinite' },
      '& $bar2': { animationDelay: '150ms' },
    },
    // The level drives `scale`, which adds to the animated transforms.
    listening: {
      '& $halo': {
        animation: 'none',
        opacity: 'calc(.6 + var(--level, 0) * .4)',
        scale: 'calc(1 + var(--level, 0) * .4)',
        transition: 'scale 120ms linear, opacity 120ms linear',
      },
      '& $core': { scale: 'calc(1 + var(--level, 0) * .07)', transition: 'scale 120ms linear' },
      '& $ring': { animationDuration: '2.4s' },
      '& $eye': { animation: 'none' },
    },
    speaking: {
      '& $halo': {
        animation: 'none',
        opacity: 'calc(.6 + var(--level, 0) * .4)',
        scale: 'calc(1 + var(--level, 0) * .4)',
        transition: 'scale 120ms linear, opacity 120ms linear',
      },
      '& $core': { scale: 'calc(1 + var(--level, 0) * .05)', transition: 'scale 120ms linear' },
      '& $ring': { animationDuration: '3.5s' },
      '& $bar': { animation: '$wave 1.4s ease-in-out infinite' },
      '& $bar2': { animationDelay: '250ms' },
    },
    offline: {
      '& $core': { background: theme.palette.text.disabled, boxShadow: 'none' },
      '& $halo, & $ring, & $sheen': { display: 'none' },
      '& $eye': { animation: 'none', transform: 'scaleY(.25)' },
    },
    '@keyframes breathe': {
      '0%, 100%': { opacity: 0.55, transform: 'scale(.92)' },
      '50%': { opacity: 1, transform: 'scale(1.05)' },
    },
    '@keyframes spin': { to: { transform: 'rotate(360deg)' } },
    '@keyframes blink': {
      '0%, 93%, 100%': { transform: 'scaleY(1)' },
      '96%': { transform: 'scaleY(.12)' },
    },
    '@keyframes sheen': {
      '0%, 55%': { transform: 'translateX(-130%)' },
      '85%, 100%': { transform: 'translateX(130%)' },
    },
    '@keyframes wave': {
      '0%, 100%': { transform: 'translateY(0)' },
      '50%': { transform: 'translateY(-14%)' },
    },
    '@keyframes pop': { '0%': { transform: 'scale(0)' }, '100%': { transform: 'scale(1)' } },
  });
});
